import { useEffect, useId, useState } from 'react';
import { isEntraConfigured } from '../../auth/entraConfig';
import { FEATURES } from '../../config/features';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';

/**
 * Confirmation before a watermarked package download. With `mfaRequired`
 * (default FEATURES.bulkMfa, off for now): Microsoft MFA when Entra is
 * configured, otherwise a lab confirmation code.
 */
export function MfaExportDialog({
  open,
  mode = 'selection',
  busy = false,
  error = '',
  mfaRequired = FEATURES.bulkMfa,
  onClose,
  onConfirm,
}) {
  const titleId = useId();
  const entra = mfaRequired && isEntraConfigured();
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!open) {
      setCode('');
      setLocalError('');
    }
  }, [open]);

  const confirmWithCode = () => {
    if (!mfaRequired) {
      setLocalError('');
      onConfirm({});
      return;
    }
    const trimmed = code.trim();
    if (!trimmed) {
      setLocalError('Enter the MFA confirmation code.');
      return;
    }
    setLocalError('');
    onConfirm({ code: trimmed });
  };

  const confirmWithMicrosoft = async () => {
    setLocalError('');
    try {
      const { acquireEntraMfaToken } = await import('../../auth/entraClient');
      const token = await acquireEntraMfaToken();
      onConfirm({ token });
    } catch (requestError) {
      const message = requestError?.message || 'MFA confirmation was cancelled.';
      if (/user_cancelled|user canceled|cancelled|canceled/i.test(message)) {
        setLocalError('MFA confirmation was cancelled.');
        return;
      }
      setLocalError(message);
    }
  };

  const shownError = localError || error;

  return (
    <Modal
      open={open}
      onClose={busy ? undefined : onClose}
      title={mfaRequired ? 'Confirm MFA to export' : 'Export watermarked copies'}
      description="The download contains watermarked copies only."
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {entra ? (
            <Button variant="navy" type="button" onClick={confirmWithMicrosoft} disabled={busy}>
              {busy ? 'Working…' : 'Confirm with Microsoft MFA'}
            </Button>
          ) : (
            <Button variant="navy" type="button" onClick={confirmWithCode} disabled={busy}>
              {busy ? 'Working…' : 'Download watermarked copies'}
            </Button>
          )}
        </>
      }
    >
      <p id={titleId} className="text-sm leading-relaxed text-[#1F2933]">
        {mode === 'import'
          ? 'This CSV selects packages by id or reference. The download is a zip of watermarked copies. Document files are not imported, and the sealed vault record is not included.'
          : 'The download is a zip of watermarked copies for Certified packages with Approved authority. The sealed vault record is not included.'}
      </p>
      {mfaRequired ? (
        <p className="mt-3 text-sm text-[#5A6A7A]">
          Provider and Custodian must confirm with multi-factor authentication before the file is
          created.
        </p>
      ) : null}
      {entra || !mfaRequired ? null : (
        <label className="mt-4 block text-sm font-semibold text-[#1F2933]">
          MFA confirmation code
          <input
            type="password"
            autoComplete="one-time-code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            className="mt-1 w-full rounded-md border border-[#D1D5DB] px-3 py-2 text-sm font-normal"
          />
        </label>
      )}
      {shownError ? (
        <p className="mt-3 text-sm text-umb-error" role="alert">
          {shownError}
        </p>
      ) : null}
    </Modal>
  );
}
